import axios from 'axios'
import { config } from '../assets/config/config'
import { notifyTask, signalTaskSubmitted } from './desktopNotifications';

const apiUrl = config.api.url

const createContentTask = (contentType, taskType, content, contentStatus) => {
	return axios.post(`${apiUrl}/v1/contentTask`, {
    contentType: contentType,
    taskType: [taskType],
    content: content,
    contentStatus: contentStatus
  })
}

const createAndPerformContentTask = (contentType, taskType, content, contentStatus, reHumanizeContentType = 'generic') => {
  return axios.post(`${apiUrl}/v1/contentTask/instant`, {
    contentType: contentType,
    taskType: [taskType],
    content: content,
    contentStatus: contentStatus,
    reHumanizeContentType: reHumanizeContentType
  })
}

// Create a content task without waiting for it to complete
const createBackgroundContentTask = async (contentType, taskType, content, contentStatus, reHumanizeContentType = 'generic', tone = '') => {
  // Use the new server endpoint that handles creation and background processing in one request
  const response = await axios.post(`${apiUrl}/v1/contentTask/background`, {
    contentType: contentType,
    taskType: [taskType],
    content: content,
    contentStatus: contentStatus,
    reHumanizeContentType: reHumanizeContentType,
    tone: tone
  });

  signalTaskSubmitted();
  return response.data;
}

// Poll for content task status with timeout and retry logic
const pollContentTaskStatus = (contentTaskId, interval = 2000, maxAttempts = 150) => {
  return new Promise((resolve, reject) => {
    let attempts = 0;
    const startTime = Date.now();
    const maxDuration = 5 * 60 * 1000; // 5 minutes max
    
    const checkStatus = () => {
      attempts++;
      
      // Check for timeout conditions
      if (attempts > maxAttempts) {
        reject(new Error(`Polling timeout: Task ${contentTaskId} exceeded maximum attempts (${maxAttempts})`));
        return;
      }
      
      if (Date.now() - startTime > maxDuration) {
        reject(new Error(`Polling timeout: Task ${contentTaskId} exceeded maximum duration (5 minutes)`));
        return;
      }
      
      getContentTaskById(contentTaskId)
        .then(response => {
          const task = response.data;
          
          // Check for failed status (but not content-filtered, which should be handled by the UI)
          if (task.contentStatus === 'failed' || task.contentStatus === 'error') {
            notifyTask(task);
            reject(new Error(`Task ${contentTaskId} failed with status: ${task.contentStatus}`));
            return;
          }
          
          // Check if task is complete (including content-filtered status)
          if (task.contentStatus !== 'progress' && task.contentStatus !== 'queued') {
            console.log(`[pollContentTaskStatus] Task ${contentTaskId} completed with status: ${task.contentStatus}`);
            notifyTask(task);
            resolve(task);
          } else {
            // Continue polling with exponential backoff
            const backoffInterval = Math.min(interval * Math.pow(1.1, attempts), 10000);
            setTimeout(checkStatus, backoffInterval);
          }
        })
        .catch(error => {
          // On network error, retry a few times before giving up
          if (attempts <= 3) {
            setTimeout(checkStatus, interval * 2);
          } else {
            reject(error);
          }
        });
    };
    
    // Start polling
    checkStatus();
  });
}

const getUserContentTasks = () => {
  return axios.get(`${apiUrl}/v1/contentTask`)
}

const getContentTaskById = (contentTaskId) => {
  return axios.get(`${apiUrl}/v1/contentTask/${contentTaskId}`, {})
}

const deleteContentTask = (contentTaskId) => {
  return axios.delete(`${apiUrl}/v1/contentTask/${contentTaskId}`)
}

const softDeleteContentTask = async (contentTaskId) => {
  // First get the current task to retrieve existing taskType array
  const currentTask = await getContentTaskById(contentTaskId);
  const existingTaskTypes = currentTask.data.taskType || [];
  
  // Add 'deleted' to the taskType array if not already present
  const updatedTaskTypes = existingTaskTypes.includes('deleted') 
    ? existingTaskTypes 
    : [...existingTaskTypes, 'deleted'];
  
  return axios.patch(`${apiUrl}/v1/contentTask/${contentTaskId}`, {
    taskType: updatedTaskTypes
  });
}

export const contentTaskService = {
  createContentTask,
  getUserContentTasks,
  createAndPerformContentTask,
  getContentTaskById,
  createBackgroundContentTask,
  pollContentTaskStatus,
  deleteContentTask,
  softDeleteContentTask
}